'use client';

import { useState } from 'react';
import { ParagraphDetailItem } from '@/utils/drupal-api/__generated__/graphql';
import { ImageSrcSetSize } from '@/utils/image';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { BrandConfig } from 'types/brand-config';

import { BasicHtml } from './basic_html';
import { CalloutParagraph } from './callout_paragraph';
import { Carousel } from './carousel';
import { ContactInfo } from './contact_info';
import { EmailSignup } from './email_signup';
import { Logos } from './logos';
import { Media } from './media';
import { RemoteVideo } from './remote_video';
import { Stat } from './stat';

function DetailItem({
  data,
  className,
  brand,
  imageAspect,
  imageSizes,
  unionType,
  isInverted,
  id,
}: {
  data: ParagraphDetailItem;
  className?: string;
  brand: BrandConfig;
  imageAspect?: number;
  imageSizes?: ImageSrcSetSize[];
  unionType?: string;
  isInverted?: boolean;
  id?: string;
}) {
  const { header, paragraphs, uuid } = data;
  const [collapse, setCollapse] = useState(true);
  return (
    <div
      id={id}
      className={clsx(
        'paragraphDetailItem',
        `group/detail-item`,
        'wysiwyg',
        'relative'
      )}
    >
      <div
        className={clsx(
          'sticky top-0 z-10 bg-white transition-all duration-300'
        )}
      >
        <button
          type="button"
          id={uuid}
          onClick={() => setCollapse(!collapse)}
          className="group/detail-item-btn w-full flex-1 justify-between px-0 py-4 text-left text-black-1 focus:outline-none"
        >
          <h4
            className={clsx(
              'h4',
              header ? 'visible' : 'invisible',
              collapse ? 'font-normal' : 'font-semibold'
            )}
          >
            {header}
          </h4>
          {paragraphs.length > 0 && (
            <div className="ml-2 self-start">
              <ChevronDownIcon
                className={clsx(
                  'h-5 w-5 text-black-1 transition-transform duration-300 sm:h-6 sm:w-6',
                  collapse ? '' : '-rotate-180'
                )}
              />
            </div>
          )}
        </button>
      </div>
      <div
        className={clsx(
          'overflow-hidden transition-all duration-300 ease-out',
          collapse
            ? 'max-h-0 -translate-y-2 opacity-0'
            : 'max-h-dvh translate-y-0 pb-8 opacity-100'
        )}
      >
        {paragraphs.map((paragraph, i) => {
          return (
            <div key={`${paragraph.uuid}-${i}-paragraph`}>
              {paragraph.__typename == 'ParagraphBasicHtml' && (
                <BasicHtml
                  data={paragraph}
                  className={clsx(className, 'py-0')}
                />
              )}
              {paragraph.__typename == 'ParagraphCalloutParagraph' && (
                <CalloutParagraph
                  data={paragraph}
                  className={className}
                  imageAspect={imageAspect}
                  imageSizes={imageSizes}
                  unionType={unionType}
                />
              )}
              {paragraph.__typename == 'ParagraphCarousel' && (
                <Carousel data={paragraph} className={className} />
              )}
              {paragraph.__typename == 'ParagraphContactInfo' && (
                <ContactInfo
                  data={paragraph}
                  className={className}
                  brand={brand}
                />
              )}
              {paragraph.__typename == 'ParagraphEmailSignup' && (
                <EmailSignup
                  data={paragraph}
                  className={className}
                  brand={brand}
                />
              )}
              {paragraph.__typename == 'ParagraphLogo' && (
                <Logos data={paragraph} className={className} />
              )}
              {paragraph.__typename == 'ParagraphMedia' && (
                <Media
                  data={paragraph}
                  className={className}
                  imageAspect={imageAspect}
                  imageSizes={imageSizes}
                />
              )}
              {paragraph.__typename == 'ParagraphRemoteVideo' && (
                <RemoteVideo
                  data={paragraph}
                  className={className}
                  srcSetSizes={imageSizes}
                  aspect={imageAspect}
                />
              )}
              {paragraph.__typename == 'ParagraphStat' && (
                <Stat
                  data={paragraph}
                  className={className}
                  isInverted={isInverted}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default DetailItem;
